import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { LightweightRuleComponent } from '../lightweight-rule/lightweight-rule.component';
import { V2ButtonComponent } from '../../button/v2-button.component';
import { ConditionPickerComponent } from '../condition-picker/condition-picker.component';
import { V2LightweightRuleEngineComponent } from '../v2-lightweight-rule-engine.component';
import { emptyRule, emptyRuleSet, isRuleSet } from '../util/functions';
import { LightWeightCondition, LightweightRule, LightweightRuleSet, RULESET_MIN_LENGTH } from '../util/types-constants';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
  selector: 'coin-lightweight-rule-set',
  templateUrl: './lightweight-rule-set.component.html',
  styleUrls: ['./lightweight-rule-set.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIconModule,
    LightweightRuleComponent,
    V2ButtonComponent,
    ConditionPickerComponent,
    TranslateModule,
    MatTooltip
  ],
  host: {
    '[class.readonly]': 'ruleEngine.disabled()',
    '[class.mat-elevation-z2]': 'true',
    '[style.background-color]': 'backgroundColor()'
  }
})
export class LightweightRuleSetComponent {
  ruleSet = input.required<LightweightRuleSet>();
  nestingDepth = input(0);
  deletable = input(true);
  delete = output<void>();

  protected ruleEngine = inject(V2LightweightRuleEngineComponent);

  backgroundColor = computed(() => (this.nestingDepth() % 2 === 0 ? 'var(--lightgrey50)' : 'var(--lightgrey100)'));

  get canDeleteItems(): boolean {
    return this.ruleSet().rules.length > RULESET_MIN_LENGTH || this.ruleEngine.allowRuleSets() === 'nested'; // always allow deleting in nestable ruleSets for easier UX
  }

  protected addRule() {
    this.ruleSet().rules.push(emptyRule());
    this.ruleEngine.registerChanges();
  }

  protected addRuleSet() {
    this.ruleSet().rules.push(emptyRuleSet({ prefillRules: this.ruleEngine.allowRuleSets() !== 'nested' }));
    this.ruleEngine.registerChanges();
  }

  protected deleteItem(ruleOrRuleSet: LightweightRule | LightweightRuleSet) {
    this.ruleSet().rules = this.ruleSet().rules.filter(existingRule => existingRule !== ruleOrRuleSet);
    this.ruleEngine.registerChanges();
  }

  protected setCondition(condition: LightWeightCondition) {
    this.ruleSet().condition = condition;
    this.ruleEngine.registerChanges();
  }

  protected onDeleteClick() {
    if (!this.deletable()) return;
    this.delete.emit();
  }

  protected readonly isRuleSet = isRuleSet;
}
